import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * vomFASS — Automatic free gift with purchase.
 *
 * Shopify "Buy X get Y" automatic discounts do NOT add product Y to the cart on
 * their own — the customer only gets it free if it is already in the cart. This
 * module guarantees that presence: when the qualifying subtotal reaches a tier's
 * threshold it adds the gift line (tagged with a `_gift` line-item property), and
 * removes it again when the cart drops below the threshold. The Shopify discount
 * is the source of truth for making the line free at checkout.
 *
 * Customers may decline the gift: the cart's remove button on a gift line carries
 * a `data-vf-gift-dismiss` attribute (see snippets/cart-products.liquid). A click
 * on it records the tier as dismissed in sessionStorage, and dismissed tiers are
 * never (re-)added for the rest of the browser session.
 *
 * Configuration is rendered server-side into a `#vf-auto-gift-config` JSON tag by
 * `snippets/vf-auto-gift.liquid` (driven by `vf_gift_tier` metaobjects), e.g.:
 *
 *   {
 *     "mode": "all",                // "all" = every qualifying tier, "highest" = top tier only
 *     "tiers": [
 *       { "id": "tier-0", "threshold": 6500, "variantId": 123, "handle": "...", "label": "Gratis Geschenk", "fence": [], "codes": [] }
 *     ]
 *   }
 *
 * `threshold` is in the store's minor currency unit (cents), matching cart line
 * prices. `fence` is an optional list of product handles: when non-empty the tier
 * qualifies purely on one of those products being in the cart (the subtotal
 * threshold is then ignored) — used e.g. to tie a gift to the Adventskalender.
 * `codes` is an optional list of discount codes (lowercased): while one of them
 * is on the cart the tier qualifies in addition — for any product and any cart
 * size (at least one non-gift product required), regardless of fence or threshold.
 * A tier with codes but no fence and a threshold of 0 is code-only. Codes are read from the cart's
 * `discount_codes`, including ones Shopify still reports as not applicable (a
 * "Buy X get Y" code only becomes applicable once the gift line is present).
 * Entering such a code also lifts an earlier dismissal of that tier.
 * The whole module is best-effort: any failure leaves the cart untouched rather
 * than blocking the customer.
 */

const CONFIG_ELEMENT_ID = 'vf-auto-gift-config';
/** Identifies cart updates this module triggered, so it ignores its own events. */
const EVENT_SOURCE = 'vf-auto-gift';
/** Line-item property key marking a line as an auto-added gift. */
const GIFT_PROPERTY = '_gift';
/** sessionStorage key holding the tier ids the customer has dismissed. */
const DISMISSED_STORAGE_KEY = 'vf-auto-gift:dismissed';

class VfAutoGift {
  /** @type {{ mode: string, tiers: Array<{ id: string, threshold: number, variantId?: number, handle?: string, label?: string, fence?: string[], codes?: string[] }> } | null} */
  #config = null;

  /** Discount codes seen on the cart at the last reconcile, to detect newly entered ones. */
  #lastCodes = new Set();

  /** False until the first reconcile has recorded the codes already on the cart. */
  #codesSeen = false;

  /** Guards against re-entrant reconciliation (our own add/remove re-fires `cart:update`). */
  #busy = false;

  /** Set when a cart update arrives mid-reconciliation, so we settle once more afterwards. */
  #pending = false;

  constructor() {
    this.#config = this.#readConfig();
    if (!this.#config || !Array.isArray(this.#config.tiers) || this.#config.tiers.length === 0) return;

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    // Applying or removing a discount code fires `discount:update`, not `cart:update`.
    document.addEventListener(ThemeEvents.discountUpdate, this.#onDiscountUpdate);

    // Capture phase so the dismissal is recorded before the cart component
    // processes the same click and fires the resulting `cart:update`.
    document.addEventListener('click', this.#onDismissClick, true);

    // Reconcile once on load so a returning cart that already qualifies gets its
    // gift (or sheds an orphaned one) without waiting for a fresh cart action.
    this.#reconcile();
  }

  /**
   * @returns {{ mode: string, tiers: Array<object> } | null}
   */
  #readConfig() {
    const element = document.getElementById(CONFIG_ELEMENT_ID);
    if (!element) return null;
    try {
      return JSON.parse(element.textContent || '');
    } catch {
      return null;
    }
  }

  /**
   * @param {Event} event
   */
  #onDismissClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-vf-gift-dismiss]') : null;
    const tierId = target?.getAttribute('data-vf-gift-dismiss');
    if (tierId) this.#dismissTier(tierId);
  };

  /**
   * @returns {Set<string>} Tier ids the customer has declined this session.
   */
  #dismissedTiers() {
    try {
      const raw = sessionStorage.getItem(DISMISSED_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
    } catch {
      return new Set();
    }
  }

  /**
   * @param {string} tierId
   */
  #dismissTier(tierId) {
    try {
      const dismissed = this.#dismissedTiers();
      dismissed.add(String(tierId));
      sessionStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify([...dismissed]));
    } catch {
      // Storage unavailable (e.g. blocked): worst case the gift is re-added.
    }
  }

  /**
   * @param {CartUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    // Ignore the `cart:update` we dispatch ourselves to avoid an infinite loop.
    if (event?.detail?.data?.source === EVENT_SOURCE) return;
    this.#reconcile();
  };

  #onDiscountUpdate = () => {
    this.#reconcile();
  };

  /**
   * Brings the cart's gift lines in sync with the qualifying subtotal.
   */
  async #reconcile() {
    if (this.#busy) {
      this.#pending = true;
      return;
    }
    this.#busy = true;

    try {
      const cart = await this.#getCart();
      if (!cart || !Array.isArray(cart.items)) return;

      const giftLines = cart.items.filter((item) => this.#giftTierId(item) != null);
      const nonGiftLines = cart.items.filter((item) => this.#giftTierId(item) == null);
      const subtotal = nonGiftLines.reduce(
        (sum, item) => sum + (item.original_line_price ?? item.line_price ?? 0),
        0
      );
      // Product handles present in the cart (gifts excluded), for fence matching.
      const presentHandles = new Set(
        nonGiftLines.map((item) => String(item.handle || '').toLowerCase()).filter(Boolean)
      );

      // Discount codes on the cart (lowercased), applicable or not.
      const presentCodes = new Set(
        (Array.isArray(cart.discount_codes) ? cart.discount_codes : [])
          .map((discount) => String(discount?.code || '').toLowerCase())
          .filter(Boolean)
      );

      // A code entered since the last check is an explicit wish for its gift, so
      // it lifts an earlier dismissal of the tiers it triggers.
      // Codes already on the cart at page load are not "new" (a reload must not
      // undo a dismissal).
      const newCodes = this.#codesSeen ? [...presentCodes].filter((code) => !this.#lastCodes.has(code)) : [];
      this.#lastCodes = presentCodes;
      this.#codesSeen = true;
      if (newCodes.length > 0) {
        for (const tier of this.#config?.tiers ?? []) {
          if (this.#tierCodes(tier).some((code) => newCodes.includes(code))) this.#undismissTier(tier.id);
        }
      }

      // Tiers the customer explicitly removed are no longer desired: they are
      // not re-added, and a still-present line (e.g. removal request failed) is
      // cleaned up by the removal pass below.
      const dismissed = this.#dismissedTiers();
      // A gift needs a real purchase: with no other product in the cart, no tier
      // qualifies (so emptying the cart also removes the gift).
      const desiredTiers = (nonGiftLines.length > 0 ? this.#desiredTiers(subtotal, presentHandles, presentCodes) : []).filter(
        (tier) => !dismissed.has(String(tier.id))
      );
      const desiredIds = new Set(desiredTiers.map((tier) => tier.id));

      let changed = false;

      // Remove gift lines that no longer qualify, target the wrong variant, or
      // reference a tier that no longer exists in the config.
      for (const line of giftLines) {
        const tierId = this.#giftTierId(line);
        const tier = this.#config?.tiers.find((candidate) => candidate.id === tierId);
        const variantMismatch = tier?.variantId != null && line.variant_id !== tier.variantId;

        if (!tier || !desiredIds.has(tierId) || variantMismatch) {
          if (await this.#removeLine(line.key)) changed = true;
        }
      }

      // Add gift lines that should be present but are missing.
      for (const tier of desiredTiers) {
        const alreadyPresent = giftLines.some(
          (line) =>
            this.#giftTierId(line) === tier.id && (tier.variantId == null || line.variant_id === tier.variantId)
        );
        if (alreadyPresent) continue;

        const variantId = tier.variantId ?? (await this.#resolveVariant(tier.handle));
        if (variantId && (await this.#addGift(variantId, tier))) changed = true;
      }

      if (changed) {
        const fresh = await this.#getCart();
        if (fresh) {
          document.dispatchEvent(
            new CartUpdateEvent(fresh, EVENT_SOURCE, {
              source: EVENT_SOURCE,
              itemCount: fresh.item_count,
            })
          );
        }
      }
    } catch {
      // Best-effort: never let a gift hiccup interfere with the cart.
    } finally {
      this.#busy = false;
      if (this.#pending) {
        this.#pending = false;
        this.#reconcile();
      }
    }
  }

  /**
   * Resolves which tiers should currently have a gift in the cart.
   * @param {number} subtotal - Qualifying subtotal in cents (gifts excluded).
   * @param {Set<string>} presentHandles - Product handles in the cart (gifts excluded), lowercased.
   * @param {Set<string>} presentCodes - Discount codes on the cart, lowercased.
   * @returns {Array<{ id: string, threshold: number, variantId?: number, handle?: string }>}
   */
  #desiredTiers(subtotal, presentHandles, presentCodes) {
    const tiers = this.#config?.tiers ?? [];
    const qualifying = tiers.filter((tier) => this.#tierQualifies(tier, subtotal, presentHandles, presentCodes));

    if (this.#config?.mode === 'highest') {
      if (qualifying.length === 0) return [];
      return [qualifying.reduce((best, tier) => (tier.threshold > best.threshold ? tier : best))];
    }

    return qualifying;
  }

  /**
   * A tier qualifies when one of its discount codes is on the cart (any product,
   * any cart size). Otherwise it qualifies either by its product fence or by the
   * subtotal threshold. When a fence is set it takes over entirely: the tier
   * qualifies only while one of its fenced products is in the cart, and the
   * threshold is ignored. Without a fence, the classic subtotal-threshold rule
   * applies.
   * @param {{ threshold: number, fence?: string[], codes?: string[] }} tier
   * @param {number} subtotal - Qualifying subtotal in cents.
   * @param {Set<string>} presentHandles - Product handles in the cart, lowercased.
   * @param {Set<string>} presentCodes - Discount codes on the cart, lowercased.
   * @returns {boolean}
   */
  #tierQualifies(tier, subtotal, presentHandles, presentCodes) {
    const codes = this.#tierCodes(tier);
    if (codes.some((code) => presentCodes.has(code))) return true;

    const fence = Array.isArray(tier.fence)
      ? tier.fence.filter(Boolean).map((handle) => String(handle).toLowerCase())
      : [];

    if (fence.length > 0) {
      return fence.some((handle) => presentHandles.has(handle));
    }

    // Code-only tier: with codes but no fence and no positive threshold, the
    // gift is triggered by the codes alone — never "every cart" by default.
    if (codes.length > 0 && !(tier.threshold > 0)) return false;

    return Number.isFinite(tier.threshold) && subtotal >= tier.threshold;
  }

  /**
   * @param {{ codes?: string[] }} tier
   * @returns {string[]} The tier's trigger codes, lowercased.
   */
  #tierCodes(tier) {
    return Array.isArray(tier?.codes)
      ? tier.codes.filter(Boolean).map((code) => String(code).trim().toLowerCase())
      : [];
  }

  /**
   * @param {string} tierId
   */
  #undismissTier(tierId) {
    try {
      const dismissed = this.#dismissedTiers();
      if (!dismissed.delete(String(tierId))) return;
      sessionStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify([...dismissed]));
    } catch {
      // Storage unavailable: nothing was persisted, so nothing to lift.
    }
  }

  /**
   * Returns the gift tier id of a cart line, or null if it is not a gift line.
   * @param {{ properties?: Record<string, string> }} item
   * @returns {string | null}
   */
  #giftTierId(item) {
    const value = item?.properties?.[GIFT_PROPERTY];
    return value ? String(value) : null;
  }

  /**
   * @returns {Promise<any | null>}
   */
  async #getCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    return response.ok ? response.json() : null;
  }

  /**
   * @param {number} variantId
   * @param {{ id: string, label?: string }} tier
   * @returns {Promise<boolean>}
   */
  async #addGift(variantId, tier) {
    /** @type {Record<string, string>} */
    const properties = { [GIFT_PROPERTY]: tier.id };
    // Hidden (`_`-prefixed) label property so the cart badge can show the tier
    // text without it appearing in the normal line-item properties list.
    if (tier.label) properties._gift_label = tier.label;

    const body = JSON.stringify({
      items: [{ id: variantId, quantity: 1, properties }],
    });
    const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
    return response.ok;
  }

  /**
   * @param {string} key - The cart line key.
   * @returns {Promise<boolean>}
   */
  async #removeLine(key) {
    const body = JSON.stringify({ id: key, quantity: 0 });
    const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
    return response.ok;
  }

  /**
   * Fallback variant lookup when a tier ships only a product handle.
   * @param {string | undefined} handle
   * @returns {Promise<number | null>}
   */
  async #resolveVariant(handle) {
    if (!handle) return null;
    try {
      const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
      const response = await fetch(`${root}/products/${handle}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      const product = await response.json();
      const variant = product.variants?.find((candidate) => candidate.available) || product.variants?.[0];
      return variant ? variant.id : null;
    } catch {
      return null;
    }
  }
}

if (!window.__vfAutoGiftInitialized) {
  window.__vfAutoGiftInitialized = true;
  const start = () => new VfAutoGift();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
}
